import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import IntegerField from '../../../core/components/Fields/IntegerField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH } from '../../constants.ts';
import { GET_SAP_BUSINESS_ONE_INTEGRATIONS, UPDATE_SAP_BUSINESS_ONE_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    companyDb: '',
    username: '',
    password: '',
    restServiceUrl: '',
    maxRecordsPerData: 0,
    avoidDeliveryDatesInThePast: false,
    orderChannelIds: [],
    clientQuery: '',
    itemQuery: '',
    overwriteDefaultShippingMethod: null,
    bpPropertyForAutomation: '',
    bpNoAutomationNoteText: '',
    calculatePriceOnOrderCreate: false,
    customUnitPriceMasterDataField: '',
    uploadSourceFile: false,
    uploadDocumentTypeField: '',
    uploadDocumentTypeEmail: '',
    uploadDocumentTypeDeliveryNote: '',
    uploadDocumentTypeOrder: '',
};

const SapBusinessOneIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('sapbusinessone.title')}`;

    const { data, refetch } = useQuery(GET_SAP_BUSINESS_ONE_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.sapBusinessOneIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                companyDb: integration.companyDb,
                username: integration.username,
                restServiceUrl: integration.restServiceUrl,
                maxRecordsPerData: integration.maxRecordsPerData,
                avoidDeliveryDatesInThePast: integration.avoidDeliveryDatesInThePast,
                orderChannelIds: integration.orderChannels.map((c) => c.id),
                itemQuery: integration.itemQuery,
                clientQuery: integration.clientQuery,
                overwriteDefaultShippingMethod: integration.overwriteDefaultShippingMethod,
                bpPropertyForAutomation: integration.bpPropertyForAutomation,
                bpNoAutomationNoteText: integration.bpNoAutomationNoteText,
                calculatePriceOnOrderCreate: integration.calculatePriceOnOrderCreate,
                customUnitPriceMasterDataField: integration.customUnitPriceMasterDataField,
                uploadSourceFile: integration.uploadSourceFile,
                uploadDocumentTypeField: integration.uploadDocumentTypeField,
                uploadDocumentTypeEmail: integration.uploadDocumentTypeEmail,
                uploadDocumentTypeDeliveryNote: integration.uploadDocumentTypeDeliveryNote,
                uploadDocumentTypeOrder: integration.uploadDocumentTypeOrder,
            });
        }
    }, [integration]);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_SAP_BUSINESS_ONE_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    companyDb: formData.companyDb,
                    username: formData.username,
                    password: formData.password,
                    maxRecordsPerData: parseInt(formData.maxRecordsPerData),
                    restServiceUrl: formData.restServiceUrl,
                    avoidDeliveryDatesInThePast: formData.avoidDeliveryDatesInThePast,
                    clientQuery: formData.clientQuery,
                    itemQuery: formData.itemQuery,
                    orderChannels: formData.orderChannelIds,
                    overwriteDefaultShippingMethod: parseInt(formData.overwriteDefaultShippingMethod),
                    bpPropertyForAutomation: formData.bpPropertyForAutomation,
                    bpNoAutomationNoteText: formData.bpNoAutomationNoteText,
                    calculatePriceOnOrderCreate: formData.calculatePriceOnOrderCreate,
                    customUnitPriceMasterDataField: formData.customUnitPriceMasterDataField,
                    uploadSourceFile: formData.uploadSourceFile,
                    uploadDocumentTypeField: formData.uploadDocumentTypeField,
                    uploadDocumentTypeEmail: formData.uploadDocumentTypeEmail,
                    uploadDocumentTypeDeliveryNote: formData.uploadDocumentTypeDeliveryNote,
                    uploadDocumentTypeOrder: formData.uploadDocumentTypeOrder,
                },
            });
            await refetch();
            publishToast({ description: t('sapbusinessone.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sapbusinessone.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('sapbusinessone.title'), to: url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH) },
                            { label: integration?.name || t('sapbusinessone.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleSubmit)}
                        title={t('sapbusinessone.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sapbusinessone.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="companyDb"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sapbusinessone.form.companyDb')} error={formErrors.companyDb}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="username"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sapbusinessone.form.username')} error={formErrors.username}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="password"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.password')}
                                    error={formErrors.password}
                                    helpText={t('secretUpdateOnly')}
                                >
                                    <SecretField
                                        {...field}
                                        secretName="sapBusinessOnePassword"
                                        secretId={integration?.id}
                                        defaultIsVisible
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="restServiceUrl"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.restServiceUrl')}
                                    error={formErrors.restServiceUrl}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="maxRecordsPerData"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.maxRecordsPerData')}
                                    error={formErrors.maxRecordsPerData}
                                >
                                    <IntegerField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="avoidDeliveryDatesInThePast"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.avoidDeliveryDatesInThePast')}
                                    error={formErrors.avoidDeliveryDatesInThePast}
                                >
                                    <BooleanField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="itemQuery"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sapbusinessone.form.itemQuery')} error={formErrors.itemQuery}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="clientQuery"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('sapbusinessone.form.clientQuery')} error={formErrors.clientQuery}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="overwriteDefaultShippingMethod"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.overwriteDefaultShippingMethod')}
                                    error={formErrors.maxRecordsPerData}
                                >
                                    <IntegerField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="bpPropertyForAutomation"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.bpPropertyForAutomation')}
                                    error={formErrors.bpPropertyForAutomation}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="bpNoAutomationNoteText"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.bpNoAutomationNoteText')}
                                    error={formErrors.bpNoAutomationNoteText}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="calculatePriceOnOrderCreate"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.calculatePriceOnOrderCreate')}
                                    error={formErrors.calculatePriceOnOrderCreate}
                                >
                                    <BooleanField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="customUnitPriceMasterDataField"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.customUnitPriceMasterDataField')}
                                    error={formErrors.customUnitPriceMasterDataField}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="uploadSourceFile"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.uploadSourceFile')}
                                    error={formErrors.uploadSourceFile}
                                >
                                    <BooleanField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="uploadDocumentTypeField"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.uploadDocumentTypeField')}
                                    error={formErrors.uploadDocumentTypeField}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="uploadDocumentTypeEmail"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.uploadDocumentTypeEmail')}
                                    error={formErrors.uploadDocumentTypeEmail}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="uploadDocumentTypeDeliveryNote"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.uploadDocumentTypeDeliveryNote')}
                                    error={formErrors.uploadDocumentTypeDeliveryNote}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="uploadDocumentTypeOrder"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.uploadDocumentTypeOrder')}
                                    error={formErrors.uploadDocumentTypeOrder}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('sapbusinessone.form.orderChannelIds')}
                                    error={formErrors.orderChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH)}>
                                    {t('sapbusinessone.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default SapBusinessOneIntegrationEditPage;
